import router from "@/router";
import { encrypt } from "@/utils/crypt.js";

// 深度拷贝
export function deepClone(source) {
  var sourceCopy = source instanceof Array ? [] : {};
  for (var item in source) {
    sourceCopy[item] =
      typeof source[item] === "object" ? deepClone(source[item]) : source[item];
  }
  return sourceCopy;
}

/**
 * @description 转换坐标
 * @param {number} imgWidth 图片宽度
 * @param {number} imgHeight 图片高度
 * @param {string} coordData 坐标系
 * @return {string} 返回热区坐标
 */
export function coordTransform(imgWidth, imgHeight, coordData) {
  const coordObj = JSON.parse(coordData);
  let list = [];
  coordObj.forEach((ele) => {
    list.push(
      Math.round((ele.x / 100) * imgWidth) +
        "," +
        Math.round((ele.y / 100) * imgHeight)
    );
    // list.push((parseInt(ele.x) * imgWidth) / 100 + ',' + (parseInt(ele.y) * imgHeight) / 100)
  });
  return list.join(" ");
}

/**
 * @description 取最小的x、y坐标
 * @param {string} 坐标
 * @return {object} “加密”的最终坐标
 */
export function interCoord(coord) {
  const newCoord = coord.split(" ");
  const tempX = newCoord.map((item) => item.split(",")[0]);
  const tempY = newCoord.map((item) => item.split(",")[1]);
  return {
    xCoord: Math.min(...tempX),
    yCoord: Math.min(...tempY) + 18,
  };
}

/**
 * @description 防抖(闭包)
 * @param {Function} fn 需要防抖的方法
 * @param {number}  time 防抖时间
 * @return {Function}
 * @example changeDate是方法名
 * changeDate: debounce(function () {
 *   // 此处写自己的逻辑
 * },500)
 */
export function debounce(fn, time) {
  // 等待的时间默认200ms
  let delay = time || 200;
  let timer = null;
  return function () {
    const _this = this;
    // 保存调用时传来的参数
    const args = arguments;
    // 每次事件被触发时，都清除之前的旧定时器
    if (timer) {
      clearTimeout(timer);
    }
    // 函数延迟执行
    timer = setTimeout(function () {
      fn.apply(_this, args);
    }, delay);
  };
}

/**
 * @description 节流
 * @param {*} fn 需要节流的方法
 * @param {*} interval 多久执行一次节流
 * @returns  {Function}
 * @example handleScroll是方法名
 * handleScroll:throttle(function(e) {
 *   // 此处写自己的逻辑
 * },  500)
 */
export function throttle(fn, time) {
  let last;
  let timer;
  let interval = time || 200;
  return function () {
    const _this = this;
    const args = arguments;
    let now = +new Date();
    if (last && now - last < interval) {
      clearTimeout(timer);
      timer = setTimeout(function () {
        last = now;
        fn.apply(_this, args);
      }, interval);
    } else {
      last = now;
      fn.apply(_this, args);
    }
  };
}

/**
 * 跳转详情页
 * @param {Object} data 详情页所需参数
 * @param {String} data.paperIssueNum 期号
 * @param {String} data.pageNo 版次
 * @param {String} data.newsId 新闻id
 * @param {String} data.searchParamStrList 高亮关键词
 * @param {String} path 目标路由路径，默认细览页 /detail
 */
export function jumpDetail(data, path = "/detail") {
  const { paperIssueNum, pageNo, newsId, searchParamStrList, chineseType, pageSortNo } =
    data;
  try {
    window.localStorage &&
      window.localStorage.setItem("hr_chineseType", chineseType || "");
  } catch (e) {}
  /**
   * 存入 sessionStorage，避免 URL 过长导致 431 错误。
   * hr_lastPath：来源页面路径，用于详情页顶部菜单高亮。
   * hr_searchParamStrList：搜索关键词列表，用于后端命中词高亮。
   */
  try {
    const path = window.location.pathname;
    const paramList =
      searchParamStrList !== undefined
        ? JSON.stringify(searchParamStrList)
        : null;
    window.sessionStorage && window.sessionStorage.setItem("hr_lastPath", path);
    window.sessionStorage &&
      window.sessionStorage.setItem("hr_searchParamStrList", paramList || "");
  } catch (e) {}
  const routeUrl = router.resolve({
    path,
    query: {
      newsId: newsId
        ? encrypt(newsId.replace(/<[^>]*>|<\/[^>]*>/gm, ""))
        : null,
      paperIssueNum: encrypt(paperIssueNum.replace(/<[^>]*>|<\/[^>]*>/gm, "")),
      pageNo: pageNo?encrypt(pageNo.replace(/<[^>]*>|<\/[^>]*>/gm, "")):'',
      pageSortNo: pageSortNo?encrypt(pageSortNo.replace(/<[^>]*>|<\/[^>]*>/gm, "")):'',
    },
  });
  window.open(routeUrl.href, "_blank");
}

/**
 * @description 新开窗口跳转路由（通用）：用 router.resolve 解析完整 href 后再 window.open。
 *              收敛各组件中重复的「router.resolve + window.open」样板代码，统一新开页行为。
 * @param {string|Object} location vue-router 跳转目标：'/path' 字符串 或 { path, query } 对象
 * @param {string} [target="_blank"] 目标窗口，默认新标签页
 * @returns {string} 解析后的完整 URL
 */
export function openRouteInNewWindow(location, target = "_blank") {
  const routeUrl = router.resolve(location);
  window.open(routeUrl.href, target);
  return routeUrl.href;
}

/**
 * @description 打开图书内容页（新窗口）。统一各图书检索组件跳转图书内容页的行为。
 * @param {string} bookId 图书 ID
 * @param {string|number} [chapterId] 章节 ID（可选，定位到指定章节）
 * @param {string|string[]} [keywords] 检索关键词（可选，用于详情页高亮显示）
 * @param {string|string[]} [anchor] 锚点（可选，用于详情页定位）
 * @returns {string} 解析后的完整 URL
 */
export function openBookContent(bookId, chapterId, keywords, anchor = null) {
  const query = {};
  if (chapterId !== undefined && chapterId !== null) {
    query.chapterId = chapterId;
  }
  // 支持单个关键词或关键词数组，传递时用逗号拼接
  if (keywords) {
    query.keywords = Array.isArray(keywords) ? keywords.join(',') : keywords;
  }
  if (anchor) query.anchor = anchor;

  return openRouteInNewWindow({
    path: '/book/content/' + bookId,
    query: Object.keys(query).length > 0 ? query : undefined
  });
}

/**
 * @description 截取带高亮的检索正文摘要。图书各检索组件统一使用。
 *
 * 以「第一个命中词」为锚点取 maxLength 个正文字符的窗口，保证命中词一定落在
 * 摘要里，两端被切断时补省略号。片段用 Range 提取，跨节点边界时标签由浏览器
 * 自动补全，不会出现截断处 <span> 未闭合、把省略号一起染红的问题。
 *
 * @param {string} text 正文 HTML（命中词形如 <span style='color:red'>习近平</span>）
 * @param {number} [maxLength=200] 摘要保留的正文字符数
 * @returns {string} 摘要 HTML
 */
export function truncateContent(text, maxLength = 200) {
  if (!text) return "";

  const container = document.createElement("div");
  container.innerHTML = text;

  // 文本节点 + 累计偏移。偏移按 DOM 解码后的纯文本字符计，
  // 不会像正则剥标签那样被实体（&nbsp; 等）的原文字符数带偏
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  let plainLength = 0;
  let currentNode;
  while ((currentNode = walker.nextNode())) {
    textNodes.push({
      node: currentNode,
      start: plainLength,
      end: plainLength + currentNode.nodeValue.length
    });
    plainLength += currentNode.nodeValue.length;
  }
  if (plainLength <= maxLength) return text;

  // 锚点 = 第一个命中词的位置。命中词必须落在窗口内，
  // 否则高亮会跟着截断一起丢掉（多个命中词相距很远时尤其明显）
  const highlight = Array.from(container.querySelectorAll("[style]")).find((el) =>
    /color\s*:\s*red\b/i.test(el.getAttribute("style") || "")
  );
  const anchorItem = highlight && textNodes.find((item) => highlight.contains(item.node));
  const anchor = anchorItem ? anchorItem.start : 0;

  const startPos = Math.max(0, anchor - Math.floor(maxLength / 2));
  const endPos = Math.min(plainLength, startPos + maxLength);

  // 纯文本偏移 → { 文本节点, 节点内偏移 }
  const locate = (offset) => {
    if (offset <= 0) return { node: textNodes[0].node, offset: 0 };
    for (let i = 0; i < textNodes.length; i++) {
      if (offset <= textNodes[i].end) {
        return { node: textNodes[i].node, offset: offset - textNodes[i].start };
      }
    }
    const lastItem = textNodes[textNodes.length - 1];
    return { node: lastItem.node, offset: lastItem.node.nodeValue.length };
  };

  const start = locate(startPos);
  const end = locate(endPos);
  const range = document.createRange();
  range.setStart(start.node, start.offset);
  range.setEnd(end.node, end.offset);

  const holder = document.createElement("div");
  holder.appendChild(range.cloneContents());

  return (startPos > 0 ? "..." : "") + holder.innerHTML + (endPos < plainLength ? "..." : "");
}

/*
 * @param {string} value 待处理的字符串
 */
export function removeTab(value) {
  if (!value) {
    return "";
  }
  return value.replace(/<[^>]*>|<\/[^>]*>/gm, "");
}

/**
 * 根据key值返回日期的格式化方式
 * @param {string} key 标志(year年、month年月、day年月日)
 * @returns 返回日期的格式化方式(YYYY年、YYYY年MM月、YYYY年MM月)
 */
export function dateTransform(key) {
  switch (key) {
    case "year":
      return "YYYY年";
    case "month":
      return "YYYY年MM月";
    case "day":
      return "YYYY年MM月DD日";
    default:
      return "";
  }
}

/**
 * @description 跳转到年鉴概览页
 * @param {string} pageId 版面ID
 */
export function jumpYearbookPage(pageId) {
  if (pageId) {
    openRouteInNewWindow({ path: '/journal/journalDetail?id=' + pageId })
  }
}
